import React, { useEffect, useRef } from "react"
import styled from "styled-components"
import { Path, MagentoPath } from "../../navigation/constants/routes"
import { AppFunctionComponent } from "../../types"
import { NeutralTextLink } from "../../ui/components/link/link.component"

interface Element {
  title?: string
  link?: Path | MagentoPath | string
  onClick?: any
  target?: string
}

interface Props {
  elements: Element[]
  hideModal: () => void
}

interface Elements {
  elements: Element[]
}

export const ModalRoot = styled.div`
  position: absolute;
  top: 40px;
  left: 15px;
  border-radius: 0 0 2px 2px;
  padding: 20px;
  width: 184px;
  background-color: ${({ theme }) => theme.color.backgroundInverted};
  box-shadow: 0 2px 30px 0 rgba(0, 0, 0, 0.11);
  box-sizing: border-box;
  text-shadow: 0 0 30px 0 rgba(13, 37, 62, 0.7);

  a,
  a:hover,
  a:visited {
    margin-bottom: 10px;
    display: block;

    &:last-child {
      margin-bottom: 0;
    }
  }
`

export const ModalTextLink = styled(NeutralTextLink)`
  color: ${({ theme }) => theme.color.text};
`

const useOutsideModal = (
  ref: React.RefObject<HTMLInputElement>,
  hideModal: () => void
) => {
  const handleClickOutside = (event: MouseEvent) => {
    if (ref.current && !ref.current.contains(event.target as Node)) {
      hideModal()
    }
  }

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  })
}

const ModalMenu: AppFunctionComponent<Elements> = ({ elements }) => {
  return (
    <ModalRoot>
      {elements.map((el) => (
        <ModalTextLink
          key={el.title}
          to={el.link}
          onClick={el.onClick}
          target={el.target}
        >
          {el.title}
        </ModalTextLink>
      ))}
    </ModalRoot>
  )
}

const Modal: AppFunctionComponent<Props> = ({ elements, hideModal }) => {
  const wrapperRef = useRef(null)
  useOutsideModal(wrapperRef, hideModal)

  return (
    <div ref={wrapperRef}>
      <ModalMenu elements={elements} />
    </div>
  )
}

export default Modal
